import { call, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga'; // ✅ Import from 'redux-saga'
import { RootState } from 'store';
import { BuildEntityParams, CalculationProcessStep, iAssemblyModel } from 'store/types/assembler';
import { AxiosRequestHeaders } from 'axios';
import reqProvider from '../coreRequests';
import { ASSEMBLER_API, SEARCH_API } from '../urlPaths';
import { ContextSearchResults } from 'store/types/searchResults';
import { iSingleEntityInstanceData} from 'store/types';
import { Variant } from 'store/types/searchResults';
import { getToken } from 'config/lib'


import { mapReduxStateToCalculationRequest } from 'utils/assemblerMapper';

export function* buildAssembly(): SagaIterator {
  const token = getToken()
  const requestPath = `${ASSEMBLER_API}/assembly/calculate`;
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json', Authorization: `Bearer ${token}`
  };

  try {
    const assemblyModel = (yield select(
      (state: RootState) => state.assemblyModel.assemblyModel
    )) as iAssemblyModel;

    const mappedPayload = mapReduxStateToCalculationRequest(assemblyModel);
    const response = yield call(reqProvider.postReq, requestPath, headers, mappedPayload);

    return response.data;
  } catch (error) {
    console.error("Error in postAssembly:", error);
    return null;
  }
}

export function* saveAssemblyModel(uuid: string): SagaIterator {
  const token = getToken()
  const requestPath = `${ASSEMBLER_API}/assembly/${uuid}`;
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json', Authorization: `Bearer ${token}`
  };

  try {
    // 1) Retrieve assemblyModel from Redux store
    const assemblyModel = (yield select(
      (state: RootState) => state.assemblyModel.assemblyModel
    )) as iAssemblyModel;

    const requestBody = {
      assemblyModel: assemblyModel.assemblyModel,
    }

    // 2) Make the POST request
    const response = yield call(reqProvider.patchReq, requestPath, headers, requestBody);
    // 3) Return or dispatch the response data
    return response.data.data;
  } catch (error) {
    console.error("Error in postAssembly:", error);
    return null;
  }
}

export function* renameAssembly(uuid: string, name: string): SagaIterator {
  const token = getToken()
  const requestPath = `${ASSEMBLER_API}/assembly/${uuid}`;
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json', Authorization: `Bearer ${token}`
  };

  try {

    const requestBody = {
      name: name,
    }

    // 2) Make the POST request
    const response = yield call(reqProvider.patchReq, requestPath, headers, requestBody);
    // 3) Return or dispatch the response data
    return response.data.data;
  } catch (error) {
    console.error("Error in postAssembly:", error);
    return null;
  }
}

export function* createNewAssembly(name: string, calculationProcessChain?: CalculationProcessStep[]): SagaIterator {
  const token = getToken()
  const requestPath = `${ASSEMBLER_API}/assembly`;
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json', Authorization: `Bearer ${token}`
  };

  const model = {calculationProcessChain: calculationProcessChain}
  const newAssembly = {
    name: name,
    model: model ?? {}
  }

  try {
    // 2) Make the POST request
    const response = yield call(reqProvider.postReq, requestPath, headers, newAssembly);

    // 3) Return or dispatch the response data
    return response.data;
  } catch (error) {
    console.error("Error in postAssembly:", error);
    return null;
  }
}

export function* getAssemblyListing(): SagaIterator {
  const token = getToken()
  const requestPath = `${ASSEMBLER_API}/assemblies`;
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json', Authorization: `Bearer ${token}`
  };

  try {
    // 2) Make the POST request
    const response = yield call(reqProvider.getReq, requestPath, headers);

    // 3) Return or dispatch the response data
    return response;
  } catch (error) {
    console.error("Error in getAssemblies:", error);
    return null;
  }
}

export function* getAssembly(uuid: string): SagaIterator {
  const token = getToken()
  const requestPath = `${ASSEMBLER_API}/assembly/${uuid}`;
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json', Authorization: `Bearer ${token}`
  };

  try {
    // 2) Make the POST request
    const response = yield call(reqProvider.getReq, requestPath, headers);

    // 3) Return or dispatch the response data
    return response.data;
  } catch (error) {
    console.error("Error in getAssemblies:", error);
    return null;
  }
}

export function* getPreMadeAssembly(sid: string, p: BuildEntityParams): SagaIterator {
  const token = getToken()
  const { countryCode, recyclingRatio, variant } = p
  const query = new URLSearchParams()
  if (countryCode !== undefined && countryCode !== null && countryCode !== '') {
    query.set('countryCode', String(countryCode))
  }
  if (recyclingRatio !== undefined && recyclingRatio !== null) {
    query.set('recyclingRatio', String(recyclingRatio))
  }
  // Variants are standard-parts only; omit for electronics (s-etr-*)
  if (variant !== undefined && variant !== null && variant !== '') {
    query.set('variant', String(variant))
  }
  const qs = query.toString()
  const requestPath = `${SEARCH_API}/build/entity/${sid}${qs ? `?${qs}` : ''}`
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json', Authorization: `Bearer ${token}`
  };
  try {
    // 2) Make the POST request
    const response = yield call(reqProvider.getReq, requestPath, headers);

    // 3) Return or dispatch the response data
    return response;
  } catch (error) {
    console.error("Error in getAssemblies:", error);
    return null;
  }
}

export function* getVariants(sid: string): SagaIterator {
  const token = getToken()
  const requestPath = `${SEARCH_API}/build/variants/${sid}` // <-- fixed typo: "variants" not "vairants"
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }

  try {
    const response = yield call(reqProvider.getReq, requestPath, headers);
    // Depending on your API response shape
    return response;
  } catch (error) {
    console.error('Error fetching variants:', error)
    throw error
  }
}

export function* deleteAssembly(uuid: string): SagaIterator {
  const token = getToken()
  const requestPath = `${ASSEMBLER_API}/assembly/${uuid}`;
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json', Authorization: `Bearer ${token}`
  };

  try {
    // 2) Make the POST request
    const response = yield call(reqProvider.deleteReq, requestPath, headers);
    // 3) Return or dispatch the response data
    return response.data;
  } catch (error) {
    console.error("Error in deleteAssembly:", error);
    return null;
  }
}

/**
 * New function to fetch search results via GET request.
 * Takes one query param: `contextName`.
 */
export function* getSearchResults(contextName: string): ContextSearchResults | any {
  const token = getToken()
const resolvedContext = contextName ?? '.';

const usesBuildResults =
  resolvedContext === '.standardParts' || resolvedContext === '.electronics'

const requestPath = usesBuildResults
    ? `${SEARCH_API}/build/results?contextName=${encodeURIComponent(resolvedContext)}`
    : `${SEARCH_API}/search/results?contextName=${encodeURIComponent(resolvedContext)}`;
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json', Authorization: `Bearer ${token}`
  };
  try {
    // 1) Make the GET request
    const response = yield call(reqProvider.getReq, requestPath, headers);
    // 2) Return the response data
    return response;
  } catch (error) {
    console.error("Error in getSearchResults:", error);
    return null;
  }
}

export function* getSingleEntity(sid: string, countryCode?: string, deliveryShapes?: string): iSingleEntityInstanceData | any {
  let requestPath = `${SEARCH_API}/search/entity/${sid}?`
  if(deliveryShapes) {
    requestPath = requestPath + `&shape=${deliveryShapes}`;
  }

  if(countryCode) {
    requestPath = requestPath + `&countryCode=${countryCode}`
  } else {
    requestPath = requestPath + `&countryCode=oecd1`
  }

  const token = getToken()

  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json', Authorization: `Bearer ${token}`
  };

  try {
    // 1) Make the GET request
    const response = yield call(reqProvider.getReq, requestPath, headers);
    // 2) Return the response data
    return response;
  } catch (error) {
    console.error("Error in getSingleEntity:", error);
    return null;
  }
}